import algoliasearch from "algoliasearch";
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next";
import getConfig from "next/config";
import {
  getServerState,
  InstantSearchServerState,
} from "react-instantsearch-core-v7";
import { CategoryTreeProvider } from "src/contexts/category-tree-context";
import { ProductCategoryView } from "src/organisms/ProductCategoryView";
import { ListingConfiguration } from "src/organisms/ProductList/ListingConfiguration";
import { TranslationProvider } from "src/providers/TranslationProvider";
import { PLPType } from "src/types/PLPType.d";
import { PRODUCTS_PUBLISH_DATE } from "src/utils/algolia";
import { renderToString } from "react-dom/server";
import { capitalize } from "instantsearch.js/es/lib/utils";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";

type NewItemsPageProps = {
  gender?: string | string[];
  categoryPageId: string;
  indexName: string;
  locale: string;
  search: {
    appId: string;
    searchKey: string;
  };
  serverState?: InstantSearchServerState;
};

export default function NewItems({
  pageProps: { gender, categoryPageId, locale, search, serverState, indexName },
}: {
  pageProps: NewItemsPageProps;
}): JSX.Element {
  const { i18n } = useLingui();

  return (
    <ProductCategoryView
      gender={gender as string}
      slug="new"
      title={t(i18n)`New items`}
      categoryPageId={categoryPageId}
      indexName={indexName}
      search={search}
      locale={locale}
      serverState={serverState}
      pageType={PLPType.NEW_IN}
    />
  );
}

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps(
  context: GetStaticPropsContext<{
    slug: string;
  }>
): Promise<GetStaticPropsResult<NewItemsPageProps>> {
  if (!context.params?.slug) {
    return {
      notFound: true,
    };
  }

  const { serverRuntimeConfig } = getConfig();

  const searchClient = algoliasearch(
    process.env.ALGOLIA_APP_ID,
    process.env.ALGOLIA_SEARCH_API_KEY
  );

  const serverState = await getServerState(
    <TranslationProvider>
      <CategoryTreeProvider>
        <ListingConfiguration
          categoryPageId={capitalize(context.params.slug)}
          gender={context.params.slug}
          hitsPerPage={72}
          indexName={`${serverRuntimeConfig.LOCALE}_${PRODUCTS_PUBLISH_DATE.DESC}`}
          searchClient={searchClient}
          pageType={PLPType.NEW_IN}
        />
      </CategoryTreeProvider>
    </TranslationProvider>,
    { renderToString }
  );

  // TEMP: We will revalidate the cache every 15min temporally.
  // Once we have backend support we will revalidate on demand.
  return {
    revalidate: 60 * 15,
    props: {
      locale: serverRuntimeConfig.LOCALE,
      gender: context.params.slug,
      categoryPageId: capitalize(context.params.slug),
      indexName: `${serverRuntimeConfig.LOCALE}_${PRODUCTS_PUBLISH_DATE.DESC}`,
      search: {
        appId: process.env.ALGOLIA_APP_ID,
        searchKey: process.env.ALGOLIA_SEARCH_API_KEY,
      },
      serverState,
    },
  };
}
